import type { FC, ReactNode } from 'react'
import type { EntryListItem } from '@/types'

import Link from 'next/link'
import { MapPin, Package } from 'lucide-react'
import { cn } from '@/lib/utils'
import KindBadge from '@/components/kb/kind-badge'
import TagChip from '@/components/kb/tag-chip'
import SourceLine from '@/components/kb/source-line'

interface EntryCardProps {
    entry: EntryListItem
    usageTerms?: string[]
    showSource?: boolean
    showPlacement?: boolean
    className?: string
}

const findUsage = (materials: string[], terms: string[]): string | null => {
    if (!terms.length) return null

    return materials.find(material => {
        const value = material.toLowerCase()

        return terms.some(term => value.includes(term))
    }) ?? null
}

const EntryCard: FC<EntryCardProps> = ({ entry, usageTerms = [], showSource = true, showPlacement = false, className }): ReactNode => {
    const usage = findUsage(entry.materials, usageTerms)
    const context = entry.context.slice(0, 6)

    return (
        <article
            className={cn(
                'group relative flex h-full flex-col gap-3 rounded-xl border-2 border-foreground bg-card p-4 shadow-brutal transition-transform duration-150 hover:-translate-y-0.5 hover:shadow-brutal-lg',
                className
            )}
        >
            <div className='flex flex-wrap items-center gap-2'>
                <KindBadge kind={entry.kind} />

                {entry.title_hi && (
                    <span className='text-xs font-medium text-muted-foreground' data-mdlfy-cms={`entries:${entry.id}:title_hi`}>
                        {entry.title_hi}
                    </span>
                )}
            </div>

            <h3 className='text-base font-bold leading-snug'>
                <Link href={`/entries/${entry.slug}`} className='after:absolute after:inset-0 after:rounded-xl group-hover:underline group-hover:decoration-2 group-hover:underline-offset-4'>
                    <span data-mdlfy-cms={`entries:${entry.id}:title`}>
                        {entry.title}
                    </span>
                </Link>
            </h3>

            {entry.summary && (
                <p className='line-clamp-3 text-sm leading-relaxed text-muted-foreground' data-mdlfy-cms={`entries:${entry.id}:summary`}>
                    {entry.summary}
                </p>
            )}

            {usage && (
                <p className='flex items-start gap-1.5 rounded-lg bg-secondary px-2.5 py-1.5 text-xs leading-relaxed'>
                    <Package className='mt-px size-4 shrink-0' aria-hidden='true' />
                    <span>
                        {usage}
                    </span>
                </p>
            )}

            {showPlacement && entry.placement && (
                <p className='flex items-start gap-1.5 text-xs leading-relaxed text-muted-foreground'>
                    <MapPin className='mt-px size-4 shrink-0' aria-hidden='true' />
                    <span className='line-clamp-2' data-mdlfy-cms={`entries:${entry.id}:placement`}>
                        {entry.placement}
                    </span>
                </p>
            )}

            {context.length > 0 && (
                <div className='flex flex-wrap gap-1.5'>
                    {context.map(tag => (
                        <TagChip key={tag.id} tag={tag} />
                    ))}
                </div>
            )}

            {showSource && (
                <div className='mt-auto border-t-2 border-dashed border-foreground/15 pt-3'>
                    <SourceLine source={entry} />
                </div>
            )}
        </article>
    )
}

export default EntryCard
